import React, { useState, useEffect } from 'react';
import { Alert, Snackbar, Box } from '@mui/material';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import WifiIcon from '@mui/icons-material/Wifi';

/**
 * OfflineIndicator Component - Detects and displays online/offline status
 * Shows a persistent banner when offline and a brief notification when coming back online
 */
const OfflineIndicator = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showOnlineSnackbar, setShowOnlineSnackbar] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setShowOnlineSnackbar(true);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setShowOnlineSnackbar(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const handleCloseOnlineSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setShowOnlineSnackbar(false);
  };

  return (
    <>
      {/* Offline Banner - Always visible when offline */}
      {!isOnline && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
          }}
          data-testid="offline-indicator"
          role="alert"
        >
          <Alert
            severity="error"
            icon={<WifiOffIcon />}
            sx={{
              borderRadius: 0,
              '& .MuiAlert-message': {
                width: '100%',
                textAlign: 'center',
              },
            }}
          >
            オフラインです。インターネット接続を確認してください。
          </Alert>
        </Box>
      )}

      {/* Online Notification - Brief notification when coming back online */}
      <Snackbar
        open={showOnlineSnackbar}
        autoHideDuration={3000}
        onClose={handleCloseOnlineSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseOnlineSnackbar}
          severity="success"
          icon={<WifiIcon />}
          sx={{ width: '100%' }}
          data-testid="online-indicator"
        >
          オンラインに戻りました
        </Alert>
      </Snackbar>
    </>
  );
};

export default OfflineIndicator;
